/**
 * Custom error classes for better error handling and debugging
 */

/**
 * Base application error class
 */
export class AppError extends Error {
  constructor(message: string, public readonly code?: string) {
    super(message);
    this.name = this.constructor.name;
    // captureStackTrace is V8-specific (Chrome, Node.js) - only available in some environments
    if ('captureStackTrace' in Error) {
      // eslint-disable-next-line @typescript-eslint/ban-types
      (Error as { captureStackTrace?: (target: object, constructor: Function) => void }).captureStackTrace?.(this, this.constructor);
    }
  }
}

/**
 * Thrown when a requested resource is not found
 * @example throw new NotFoundError('Minister with ID 123 not found')
 */
export class NotFoundError extends AppError {
  constructor(message: string) {
    super(message, 'NOT_FOUND');
  }
}

/**
 * Thrown when data validation fails
 * @example throw new ValidationError('Email format is invalid')
 */
export class ValidationError extends AppError {
  constructor(message: string, public readonly field?: string) {
    super(message, 'VALIDATION_ERROR');
  }
}

/**
 * Thrown when a database operation fails
 * @example throw new DatabaseError('Failed to connect to database')
 */
export class DatabaseError extends AppError {
  constructor(message: string, public readonly originalError?: unknown) {
    super(message, 'DATABASE_ERROR');
  }
}

/**
 * Thrown when user lacks required permissions
 * @example throw new UnauthorizedError('You do not have permission to delete this resource')
 */
export class UnauthorizedError extends AppError {
  constructor(message: string) {
    super(message, 'UNAUTHORIZED');
  }
}
